import { Field } from 'react-final-form'
import { Select } from '../../../../react-final-form-wrappers'
import { client } from '../../../../client'
import IconButton from '@material-ui/core/IconButton'
import DeleteIcon from '@material-ui/icons/Delete'
import Grid from '@material-ui/core/Grid'
import { useTranslation } from 'react-i18next'
import { useCallback } from 'react'
import { useNotificationBar } from '../../../../components/NotificationBar'
import { DateTimePicker } from './DateTimePicker'
import { ConsultationPriceField } from './ConsultationPriceField'
import PsychologistSelector from '../../../../components/PsychologistSelector/PsychologistSelector'
import * as types from '../../../../types'
import './styles.css'
import { useOptionsContext } from './optionsContext'
import { useFieldValue } from '../../../../hooks/useFieldValue'

function notUndefined<T>(x: T | undefined): x is T {
  return typeof x !== 'undefined'
}

type DurationOptionsFilter = (options: types.Duration[]) => types.Duration[]

// Sorts durations ascending by their `value` (minutes). Sort is stable, so
// entries with the same value keep their original order (e.g. No-show "-" and
// "coulance" which both have value 0).
const withSortedByValue =
  (filter: DurationOptionsFilter): DurationOptionsFilter =>
  (options) =>
    [...filter(options)].sort((a, b) => a.value - b.value)

const getDurationOptionsFilter = (
  activity: types.Activity2022,
  treatmentType: types.TreatmentType,
  consultationPrices: types.ConsultationPrice[],
  year: number
): DurationOptionsFilter => {
  // TODO: these ids definitely shouldn't be hardcoded
  if (activity.consultationType?.id === 34) {
    // Reiskosten: only 25 and 26 minutes (duration ids mapped to 35 and 36)
    return withSortedByValue((options) =>
      options.filter((option) => option.id === 35 || option.id === 36)
    )
  }
  if (activity.consultationType?.id === 3) {
    // for Intercollegiaal overleg
    return withSortedByValue((options) =>
      options.filter((option) => option.id === 1 || option.id === 2)
    )
  } else if (activity.consultationType?.id === 4) {
    // for No-show
    return withSortedByValue((options) =>
      options.filter((option) => option.id === 9 || option.id === 10)
    )
  } else if (treatmentType.id === 2) {
    // for uninsured treatment (Diagnostiek/Behandeling share same uninsured* codes)
    const uninsuredDurationIds = new Set(
      consultationPrices
        .filter(
          (p) =>
            p.year === year &&
            (p.code === 'uninsured15' ||
              p.code === 'uninsured30' ||
              p.code === 'uninsured45' ||
              p.code === 'uninsured60' ||
              p.code === 'uninsured75' ||
              p.code === 'uninsured90' ||
              p.code === 'uninsured105' ||
              p.code === 'uninsured120' ||
              p.code === 'OV0012')
        )
        .map((p) => p.duration?.id)
        .filter((id): id is number => typeof id === 'number')
    )
    return withSortedByValue((options) =>
      options.filter((o) => uninsuredDurationIds.has(o.id))
    )
  }

  // TODO: this shouldn't be recalculated for every activity
  const availablePrices = consultationPrices.filter(
    (price) =>
      price.consultationType?.id === activity.consultationType?.id &&
      price.year === year
  )
  const availableDurationIds = new Set(
    availablePrices.map((p) => p.duration?.id).filter(notUndefined)
  )
  return withSortedByValue((durations: types.Duration[]) =>
    durations.filter((d) => availableDurationIds.has(d.id))
  )
}

type ConsultationTypeOptionsFilter = (
  types: types.ConsultationType[]
) => types.ConsultationType[]

const getConsultationTypeOptions = (
  treatmentType: types.TreatmentType
): ConsultationTypeOptionsFilter => {
  if (treatmentType.id === 2) {
    // for uninsured treatment
    return (options) => {
      return options.filter((option) => option.id !== 3)
    }
  }
  return (options) => options
}

const getDurationLabel = (duration: types.Duration): string => {
  if (duration.id === 9) return '-'
  if (duration.id === 10) return 'coulance'
  return duration.value + ' minuten'
}

const EditableActivity2022 = ({ name, activity, handleRemoveActivity }) => {
  const { t } = useTranslation('timeRegistrationSection')
  const { showMessage, NotificationBar } = useNotificationBar({})

  const {
    consultationTypeOptions,
    occupationTypeOptions,
    settingTypeOptions,
    durationOptions,
    consultationPrices,
    treatmentType
  } = useOptionsContext()

  return (
    <Grid container spacing={1}>
      <Grid item xs={3}>
        <Field
          name={`${name}.date`}
          component={DateTimePicker as any}
          disabled={!!activity.invoiceRow}
          onClick={() =>
            !!activity.invoiceRow && showMessage(t('notEditableInputText'))
          }
        />
      </Grid>

      <Grid item xs={2}>
        <Select
          options={consultationTypeOptions}
          optionsFilter={getConsultationTypeOptions(treatmentType)}
          getOptionLabel={(i) => i.name}
          getOptionValue={(i) => i.id}
          name={`${name}.consultationType.id`}
          disabled={!!activity.invoiceRow}
          onClick={() =>
            !!activity.invoiceRow && showMessage(t('notEditableInputText'))
          }
        />
      </Grid>

      <Grid item xs={2}>
        <PsychologistSelector
          name={`${name}.psychologist.id`}
          disabled={!!activity.invoiceRow}
        />
      </Grid>

      <Grid item xs={1}>
        <Select
          options={occupationTypeOptions}
          getOptionLabel={(i) => i.name}
          getOptionValue={(i) => i.id}
          name={`${name}.occupationType.id`}
          fieldProps={{
            defaultValue: occupationTypeOptions.find(
              (option) => option.id === 5
            )?.id
          }}
          disabled={!!activity.invoiceRow}
          onClick={() =>
            !!activity.invoiceRow && showMessage(t('notEditableInputText'))
          }
        />
      </Grid>

      <Grid item xs={1}>
        <Select
          options={settingTypeOptions}
          getOptionLabel={(i) => i.name}
          getOptionValue={(i) => i.id}
          name={`${name}.settingType.id`}
          disabled={!!activity.invoiceRow}
          onClick={() =>
            !!activity.invoiceRow && showMessage(t('notEditableInputText'))
          }
        />
      </Grid>

      <Grid item xs={1}>
        <Select
          options={durationOptions}
          optionsFilter={getDurationOptionsFilter(
            activity,
            treatmentType,
            consultationPrices,
            new Date(activity.date).getFullYear()
          )}
          getOptionLabel={getDurationLabel}
          getOptionValue={(i) => i.id}
          fieldProps={{
            defaultValue: activity.consultationType?.id === 4 ? 9 : undefined
          }}
          name={`${name}.duration.id`}
          disabled={!!activity.invoiceRow}
          onClick={() =>
            !!activity.invoiceRow && showMessage(t('notEditableInputText'))
          }
        />
      </Grid>

      <Grid item xs={1}>
        <ConsultationPriceField
          rootName={name}
          disabled={!!activity.invoiceRow}
        />
      </Grid>

      <Grid item xs={1}>
        {!activity.invoiceRow && (
          <IconButton aria-label="Delete" onClick={handleRemoveActivity}>
            <DeleteIcon fontSize="small" color="disabled" />
          </IconButton>
        )}
      </Grid>
      {NotificationBar}
    </Grid>
  )
}

type Activity2022Props = {
  name: string
  index: number
  remove: (index: number) => void
}

const Activity2022 = ({
  name,
  index,
  remove
}: Activity2022Props): JSX.Element => {
  // One subscription to the activity, passed down. This component and
  // EditableActivity2022 previously subscribed to the same field separately.
  const activity = useFieldValue(name) as types.Activity2022

  const handleRemoveActivity = useCallback(() => {
    remove(index) // remove from the form
    client.removeActivity2022(activity.id) // send a request
  }, [activity.id, index, remove])

  return (
    <EditableActivity2022
      handleRemoveActivity={handleRemoveActivity}
      name={name}
      activity={activity}
    />
  )
}

export default Activity2022
